import { useEffect, useState } from "react";
import { Link, useNavigate, NavLink, Routes, Route } from "react-router-dom";
import api, { resolveImageUrl, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { LogOut, BarChart3, Layers, Building2, Users as UsersIcon, MessageSquare, Star, EyeOff, Eye, Crown, UserPlus as UserPlusIcon, X as XIcon } from "lucide-react";

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    if (!user || user === false) return null;

    return (
        <main data-testid="admin-layout" className="min-h-screen bg-black text-white">
            <header className="border-b border-white/10 px-6 md:px-10 py-5 flex items-center justify-between">
                <Link to="/" className="font-serif text-2xl">Tile<span className="text-gold">Sphere</span> <span className="text-xs tracking-[0.3em] uppercase text-white/40 ml-3">Admin</span></Link>
                <div className="flex items-center gap-4">
                    <span className="text-sm text-white/60">{user.name} · <span className="text-gold capitalize">{user.role.replace("_", " ")}</span></span>
                    <button onClick={async () => { await logout(); nav("/"); }} className="p-2 text-white/60 hover:text-gold" data-testid="admin-logout"><LogOut size={16} /></button>
                </div>
            </header>
            <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr]">
                <aside className="border-r border-white/10 p-6 lg:min-h-[calc(100vh-65px)]" data-testid="admin-sidebar">
                    <nav className="space-y-1">
                        <NavItem to="/admin" end icon={<BarChart3 size={16} />}>Dashboard</NavItem>
                        <NavItem to="/admin/projects" icon={<Layers size={16} />}>Projects</NavItem>
                        <NavItem to="/admin/companies" icon={<Building2 size={16} />}>Companies</NavItem>
                        <NavItem to="/admin/agents" icon={<UsersIcon size={16} />}>Agents</NavItem>
                        <NavItem to="/admin/inquiries" icon={<MessageSquare size={16} />}>Inquiries</NavItem>
                    </nav>
                </aside>
                <section className="p-6 md:p-10" data-testid="admin-content">
                    <Routes>
                        <Route index element={<Dashboard />} />
                        <Route path="projects" element={<ProjectsManager />} />
                        <Route path="companies" element={<CompaniesManager />} />
                        <Route path="agents" element={<AgentsManager />} />
                        <Route path="inquiries" element={<InquiriesManager />} />
                    </Routes>
                </section>
            </div>
        </main>
    );
}

function NavItem({ to, icon, children, end }) {
    return (
        <NavLink
            to={to}
            end={end}
            data-testid={`admin-nav-${String(children).toLowerCase()}`}
            className={({ isActive }) => `flex items-center gap-3 px-4 py-3 text-sm tracking-[0.18em] uppercase transition-colors ${isActive ? "bg-[#0c0c0c] text-gold border-l-2 border-gold" : "text-white/70 hover:text-gold"}`}
        >
            {icon} {children}
        </NavLink>
    );
}

function Dashboard() {
    const [stats, setStats] = useState(null);
    useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(()=>{}); }, []);
    if (!stats) return <div className="text-white/60">Loading…</div>;
    const cards = [
        { label: "Projects", value: stats.total_projects, icon: <Layers size={18} /> },
        { label: "Pending", value: stats.pending_projects, icon: <EyeOff size={18} /> },
        { label: "Published", value: stats.published_projects, icon: <Eye size={18} /> },
        { label: "Companies", value: stats.total_companies, icon: <Building2 size={18} /> },
        { label: "Agents", value: stats.total_agents, icon: <UsersIcon size={18} /> },
        { label: "Inquiries", value: stats.total_inquiries, icon: <MessageSquare size={18} /> },
    ];
    return (
        <div data-testid="admin-dashboard">
            <div className="overline mb-3">Overview</div>
            <h1 className="font-serif text-4xl tracking-tight mb-10">Showroom analytics.</h1>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12">
                {cards.map((c) => (
                    <div key={c.label} className="border border-white/10 p-6 bg-[#0a0a0a]">
                        <div className="flex items-center justify-between text-gold/70 mb-3">{c.icon}<span className="overline">{c.label}</span></div>
                        <div className="font-serif text-5xl">{c.value}</div>
                    </div>
                ))}
            </div>
            <div className="overline mb-4">Top Projects</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                {stats.top_projects.map((p) => (
                    <Link to={`/projects/${p.slug}`} key={p.slug} className="block border border-white/10 group">
                        <div className="aspect-[4/3] overflow-hidden">
                            <img src={resolveImageUrl(p.thumbnail_url)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                        </div>
                        <div className="p-3">
                            <div className="text-xs text-white/50">{p.company_name}</div>
                            <div className="font-serif text-sm leading-tight">{p.title}</div>
                            <div className="text-[10px] text-gold mt-1 tracking-[0.2em] uppercase">{p.views} views</div>
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
}

function ProjectsManager() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");
    const load = () => api.get("/projects", { params: { status: filter === "all" ? undefined : filter, limit: 200 } }).then((r) => setItems(r.data.items || []));
    useEffect(() => { load(); }, [filter]);

    const patch = async (id, body, msg) => {
        await api.patch(`/projects/${id}`, body);
        toast.success(msg || "Updated");
        load();
    };

    const remove = async (id) => {
        if (!window.confirm("Delete project?")) return;
        await api.delete(`/projects/${id}`);
        load();
    };

    return (
        <div data-testid="admin-projects">
            <div className="overline mb-3">Project Manager</div>
            <h1 className="font-serif text-4xl tracking-tight mb-8">Curate the showroom.</h1>
            <div className="flex gap-2 mb-6">
                {["all","published","pending","draft","rejected"].map((f) => (
                    <button key={f} onClick={() => setFilter(f)} data-testid={`admin-filter-${f}`} className={`text-xs px-3 py-1.5 border capitalize ${filter === f ? "bg-gold text-black border-gold" : "border-white/15 text-white/70 hover:border-gold"}`}>{f}</button>
                ))}
            </div>
            <div className="border border-white/10">
                <table className="w-full text-sm">
                    <thead className="text-left text-white/50 text-xs uppercase tracking-wider">
                        <tr>
                            <th className="px-4 py-3">Project</th>
                            <th className="px-4 py-3">Company</th>
                            <th className="px-4 py-3">Room</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3">Flags</th>
                            <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((p) => (
                            <tr key={p.id} className="border-t border-white/5" data-testid={`admin-row-${p.slug}`}>
                                <td className="px-4 py-3 flex items-center gap-3">
                                    <img src={resolveImageUrl(p.thumbnail_url)} alt="" className="w-12 h-12 object-cover" />
                                    <Link to={`/projects/${p.slug}`} className="font-serif hover:text-gold">{p.title}</Link>
                                </td>
                                <td className="px-4 py-3 text-white/70">{p.company_name}</td>
                                <td className="px-4 py-3 text-white/70">{p.room_type}</td>
                                <td className="px-4 py-3">
                                    <span className={`text-[10px] tracking-widest uppercase px-2 py-1 ${p.status === "published" ? "bg-gold/10 text-gold border border-gold/30" : p.status === "pending" ? "bg-yellow-500/10 text-yellow-400 border border-yellow-500/30" : "bg-white/5 text-white/50 border border-white/10"}`}>{p.status}</span>
                                </td>
                                <td className="px-4 py-3 text-gold flex gap-2">
                                    {p.is_featured && <Star size={14} fill="currentColor" />}
                                    {p.is_hero && <Crown size={14} />}
                                </td>
                                <td className="px-4 py-3 text-right space-x-3 whitespace-nowrap">
                                    {p.status !== "published" && <button onClick={() => patch(p.id, { status: "published" }, "Published")} className="text-gold hover:text-gold-soft text-xs uppercase tracking-wider" data-testid={`admin-publish-${p.slug}`}>Publish</button>}
                                    <button onClick={() => patch(p.id, { is_featured: !p.is_featured })} className="text-white/70 hover:text-gold text-xs uppercase tracking-wider" data-testid={`admin-feature-${p.slug}`}>{p.is_featured ? "Unfeature" : "Feature"}</button>
                                    <button onClick={() => patch(p.id, { is_hero: !p.is_hero })} className="text-white/70 hover:text-gold text-xs uppercase tracking-wider" data-testid={`admin-hero-${p.slug}`}>{p.is_hero ? "Un-hero" : "Hero"}</button>
                                    <button onClick={() => remove(p.id)} className="text-red-400/70 hover:text-red-400 text-xs uppercase tracking-wider">Delete</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function CompaniesManager() {
    const [companies, setCompanies] = useState([]);
    const [name, setName] = useState("");
    const [desc, setDesc] = useState("");
    const [loc, setLoc] = useState("");

    const load = () => api.get("/companies").then((r) => setCompanies(r.data || []));
    useEffect(() => { load(); }, []);

    const create = async (e) => {
        e.preventDefault();
        try {
            await api.post("/companies", { name, description: desc, location: loc });
            toast.success("Company added");
            setName(""); setDesc(""); setLoc("");
            load();
        } catch (err) { toast.error("Failed"); }
    };

    return (
        <div data-testid="admin-companies">
            <div className="overline mb-3">Company Manager</div>
            <h1 className="font-serif text-4xl tracking-tight mb-8">The houses we represent.</h1>
            <form onSubmit={create} className="border border-white/10 bg-[#0a0a0a] p-6 mb-8 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="admin-company-form">
                <input required value={name} onChange={(e)=>setName(e.target.value)} placeholder="Name" className="bg-black border border-white/15 px-3 py-2 col-span-1" />
                <input value={desc} onChange={(e)=>setDesc(e.target.value)} placeholder="Description" className="bg-black border border-white/15 px-3 py-2 col-span-2" />
                <input value={loc} onChange={(e)=>setLoc(e.target.value)} placeholder="Location" className="bg-black border border-white/15 px-3 py-2 col-span-1" />
                <button type="submit" className="btn-gold col-span-full md:col-span-1">Add Company</button>
            </form>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {companies.map((c) => (
                    <div key={c.id} className="border border-white/10 p-6 bg-[#0a0a0a]">
                        <div className="overline text-gold/70 mb-2">{c.location}</div>
                        <div className="font-serif text-2xl">{c.name}</div>
                        <p className="text-white/60 text-sm mt-2">{c.description}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

function AgentsManager() {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null); // null = create, object = edit
    const [form, setForm] = useState({ name: "", email: "", password: "", role: "sales_agent", company_id: "" });
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        setError("");
        try {
            const [u, c] = await Promise.all([api.get("/admin/users"), api.get("/companies")]);
            setUsers(Array.isArray(u.data) ? u.data : []);
            setCompanies(Array.isArray(c.data) ? c.data : []);
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || "Failed to load users");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const startCreate = () => {
        setEditing(null);
        setForm({ name: "", email: "", password: "", role: "sales_agent", company_id: "" });
        setShowForm(true);
    };

    const startEdit = (u) => {
        setEditing(u);
        setForm({ name: u.name || "", email: u.email || "", password: "", role: u.role, company_id: u.company_id || "" });
        setShowForm(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            if (editing) {
                const body = { name: form.name, role: form.role, company_id: form.company_id || null };
                if (form.password) body.password = form.password;
                await api.patch(`/admin/users/${editing.id}`, body);
                toast.success("User updated");
            } else {
                await api.post("/admin/users", form);
                toast.success(`Agent ${form.name} created`);
            }
            setShowForm(false);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (u) => {
        if (u.id === currentUser?.id) {
            toast.error("You cannot delete your own account");
            return;
        }
        if (!window.confirm(`Delete ${u.name} (${u.email})? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/users/${u.id}`);
            toast.success("User deleted");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed");
        }
    };

    const companyName = (id) => companies.find((c) => c.id === id)?.name || "—";

    const roleStyles = {
        super_admin: "bg-gold/10 text-gold border-gold/30",
        company_admin: "bg-blue-500/10 text-blue-300 border-blue-500/30",
        sales_agent: "bg-white/5 text-white/80 border-white/15",
    };

    return (
        <div data-testid="admin-agents">
            <div className="flex items-end justify-between mb-8">
                <div>
                    <div className="overline mb-3">Users & Agents</div>
                    <h1 className="font-serif text-4xl tracking-tight">Manage accounts.</h1>
                </div>
                <button onClick={startCreate} data-testid="admin-add-user-btn" className="btn-gold">
                    <UserPlusIcon size={16} /> Add Agent
                </button>
            </div>

            {error && (
                <div className="mb-6 border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-300" data-testid="admin-users-error">{error}</div>
            )}

            {showForm && (
                <form onSubmit={submit} className="border border-gold/30 bg-[#0a0a0a] p-8 mb-8 space-y-4" data-testid="admin-user-form">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="font-serif text-2xl">{editing ? `Edit ${editing.name}` : "Create new agent"}</h3>
                        <button type="button" onClick={() => setShowForm(false)} className="text-white/50 hover:text-gold"><XIcon size={16} /></button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <div className="overline mb-2">Full name</div>
                            <input required value={form.name} onChange={(e)=>setForm(f=>({...f,name:e.target.value}))} className="w-full bg-black border border-white/15 px-4 py-3 focus:border-gold focus:outline-none" data-testid="user-form-name" />
                        </div>
                        <div>
                            <div className="overline mb-2">Email</div>
                            <input required type="email" disabled={!!editing} value={form.email} onChange={(e)=>setForm(f=>({...f,email:e.target.value}))} className="w-full bg-black border border-white/15 px-4 py-3 focus:border-gold focus:outline-none disabled:opacity-50" data-testid="user-form-email" />
                            {editing && <div className="text-[10px] text-white/40 mt-1">Email cannot be changed</div>}
                        </div>
                        <div>
                            <div className="overline mb-2">{editing ? "New password (leave blank to keep)" : "Password (min 6)"}</div>
                            <input type="password" required={!editing} value={form.password} onChange={(e)=>setForm(f=>({...f,password:e.target.value}))} className="w-full bg-black border border-white/15 px-4 py-3 focus:border-gold focus:outline-none" data-testid="user-form-password" />
                        </div>
                        <div>
                            <div className="overline mb-2">Role</div>
                            <select value={form.role} onChange={(e)=>setForm(f=>({...f,role:e.target.value}))} className="w-full bg-black border border-white/15 px-4 py-3 focus:border-gold focus:outline-none" data-testid="user-form-role">
                                <option value="sales_agent">Sales Agent</option>
                                <option value="company_admin">Company Admin</option>
                                <option value="super_admin">Super Admin</option>
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <div className="overline mb-2">Company {form.role !== "super_admin" && <span className="text-red-400">*</span>}</div>
                            <select required={form.role !== "super_admin"} value={form.company_id} onChange={(e)=>setForm(f=>({...f,company_id:e.target.value}))} className="w-full bg-black border border-white/15 px-4 py-3 focus:border-gold focus:outline-none" data-testid="user-form-company">
                                <option value="">— Select company —</option>
                                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="flex gap-3 pt-2">
                        <button disabled={busy} type="submit" className="btn-gold disabled:opacity-50" data-testid="user-form-submit">
                            {busy ? "Saving…" : editing ? "Save Changes" : "Create Agent"}
                        </button>
                        <button type="button" onClick={() => setShowForm(false)} className="btn-ghost-gold">Cancel</button>
                    </div>
                </form>
            )}

            <div className="border border-white/10 overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-white/50">Loading users…</div>
                ) : users.length === 0 ? (
                    <div className="p-12 text-center text-white/50">No users yet.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="text-left text-white/50 text-xs uppercase tracking-wider bg-[#080808]">
                            <tr>
                                <th className="px-4 py-3">Name</th>
                                <th className="px-4 py-3">Email</th>
                                <th className="px-4 py-3">Role</th>
                                <th className="px-4 py-3">Company</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u) => (
                                <tr key={u.id} className="border-t border-white/5" data-testid={`admin-user-row-${u.email}`}>
                                    <td className="px-4 py-3 font-serif text-base">{u.name}{u.id === currentUser?.id && <span className="ml-2 text-[10px] tracking-[0.2em] uppercase text-gold/70">You</span>}</td>
                                    <td className="px-4 py-3 text-white/70">{u.email}</td>
                                    <td className="px-4 py-3">
                                        <span className={`text-[10px] tracking-widest uppercase px-2 py-1 border ${roleStyles[u.role] || ""}`}>{u.role.replace("_", " ")}</span>
                                    </td>
                                    <td className="px-4 py-3 text-white/70">{companyName(u.company_id)}</td>
                                    <td className="px-4 py-3 text-right space-x-4 whitespace-nowrap">
                                        <button onClick={() => startEdit(u)} className="text-gold hover:text-gold-soft text-xs uppercase tracking-wider" data-testid={`user-edit-${u.email}`}>Edit</button>
                                        {u.id !== currentUser?.id && (
                                            <button onClick={() => remove(u)} className="text-red-400/70 hover:text-red-400 text-xs uppercase tracking-wider" data-testid={`user-delete-${u.email}`}>Delete</button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function InquiriesManager() {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get("/inquiries").then((r) => setItems(r.data || [])).catch(()=>{}); }, []);
    return (
        <div data-testid="admin-inquiries">
            <div className="overline mb-3">Inquiries</div>
            <h1 className="font-serif text-4xl tracking-tight mb-8">Inbound interest.</h1>
            {items.length === 0 ? (
                <div className="border border-white/10 p-12 text-center text-white/50">No inquiries yet.</div>
            ) : (
                <div className="space-y-3">
                    {items.map((i) => (
                        <div key={i.id} className="border border-white/10 p-5 bg-[#0a0a0a]">
                            <div className="flex justify-between mb-2">
                                <div className="font-serif text-lg">{i.name} <span className="text-white/50 text-sm">· {i.email}</span></div>
                                <div className="text-xs text-gold tracking-[0.2em] uppercase">{i.project_title}</div>
                            </div>
                            <p className="text-white/70 text-sm">{i.message}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
